import mapboxgl from 'mapbox-gl';
import { useEffect } from 'react';

import MapBox from './MapBox.ts';
import { INITIAL_STYLE } from './constants.ts';
import { UseMapProps } from './types.ts';
import { usePinLayerCallbacks } from './usePinLayerCallbacks.ts';
import { usePinLayerCursor } from './usePinLayerCursor.ts';
import { usePolygonDraw } from './usePolygonDraw.ts';
import { useSubjectLayerCallback } from './useSubjectLayerCallback.ts';
import { ZoomToFitControl } from './ZoomToFitControl.tsx';

const useMap = <Feature = void>({
  mapContainerRef,
  mapRef,
  subject,
  center,
  isEnabled = true,
  enablePolygonDraw = false,
  showPolygonDrawControl = true,
  showTrashControl = true,
  enableStatusFiltering = false,
  statusFilterVariant = 'sales',
  selectedStatuses,
  callbacks = {},
  zoom,
  pins,
  controlsPosition = 'top-right',
  showCompass = true,
  enableGeolocate = false,
  fitPinsRef,
  controlsBottomOffsetPx,
}: UseMapProps<Feature>) => {
  useEffect(() => {
    const shouldRenderMap =
      isEnabled && mapContainerRef.current !== null && mapRef.current === null;
    if (!shouldRenderMap) return;

    const init = (instance: MapBox) => {
      const { getMap, getDraw } = instance;
      const map = getMap();
      const draw = getDraw();
      const position = controlsPosition;

      if (position === 'bottom-right') {
        if (fitPinsRef) {
          map.addControl(new ZoomToFitControl(() => fitPinsRef.current?.()), position);
        } else if (enableGeolocate) {
          map.addControl(new mapboxgl.GeolocateControl(), position);
        }
        map.addControl(new mapboxgl.FullscreenControl(), position);
        map.addControl(new mapboxgl.NavigationControl({ showCompass }), position);
      } else {
        map.addControl(new mapboxgl.NavigationControl({ showCompass }), position);
        map.addControl(new mapboxgl.FullscreenControl(), position);
        if (fitPinsRef) {
          map.addControl(new ZoomToFitControl(() => fitPinsRef.current?.()), position);
        } else if (enableGeolocate) {
          map.addControl(new mapboxgl.GeolocateControl(), position);
        }
      }

      if (enablePolygonDraw) {
        map.addControl(draw as any, position);
        if (!showPolygonDrawControl) {
          const drawEl = map.getContainer().querySelector('.mapboxgl-ctrl-group:empty');
          if (drawEl instanceof HTMLElement) {
            drawEl.style.display = 'none';
          }
        }
      }

      if (position === 'bottom-right' && controlsBottomOffsetPx != null) {
        const corner = map.getContainer().querySelector('.mapboxgl-ctrl-bottom-right');
        if (corner instanceof HTMLElement) {
          corner.style.bottom = `${controlsBottomOffsetPx}px`;
          corner.style.right = '12px';
        }
      }
    };

    mapRef.current = new MapBox(
      // @ts-expect-error `mapContainerRef.current` will not be `null` here
      mapContainerRef.current,
      [center.lng, center.lat],
      {
        styleURL: INITIAL_STYLE.url,
        zoom,
        showPolygonDrawControl,
      },
      init,
    );
  }, [
    callbacks,
    center.lat,
    center.lng,
    enablePolygonDraw,
    showPolygonDrawControl,
    mapContainerRef,
    mapRef,
    subject,
    zoom,
    isEnabled,
    pins,
    controlsPosition,
    showCompass,
    enableGeolocate,
    fitPinsRef,
    controlsBottomOffsetPx,
  ]);

  useSubjectLayerCallback({
    mapRef,
    subject,
    center,
    callbacks: {
      onMapLoad: callbacks.onMapLoad,
      onStyleLoad: callbacks.onStyleLoad,
    },
  });

  usePinLayerCallbacks({
    mapRef,
    pins,
    selectedStatuses,
    enableStatusFiltering,
    statusFilterVariant,
    callbacks: {
      onPinFilterChange: callbacks.onPinFilterChange,
      onMapLoad: callbacks.onMapLoad,
      onPinSourceLoad: callbacks.onPinSourceLoad,
      onPinClick: callbacks.onPinClick,
      onPinHoverChange: callbacks.onPinHoverChange,
      onMoveEnd: callbacks.onMoveEnd,
    },
  });

  usePolygonDraw({
    mapRef,
    isEnabled: enablePolygonDraw,
    showTrashControl,
    callbacks: {
      onPolygonDrawCreate: callbacks.onPolygonDrawCreate,
      onPolygonDrawUpdate: callbacks.onPolygonDrawUpdate,
      onPolygonDrawDelete: callbacks.onPolygonDrawDelete,
    },
  });

  usePinLayerCursor({
    mapRef,
    isEnabled: enablePolygonDraw,
  });
};

export { useMap };
